import React, { useEffect, useMemo, useRef } from 'react'
import { mutateWidgetTypeWithDefaults, WidgetConfig } from '@/components/WidgetConfiguration'
import { create } from 'zustand/index'
import { produce } from 'immer'
import { useWidgetSelectorStore } from '@/components/editor/widget-selector/widget-selector'
import { getAreaByWidgetPath, mutateAreaByWidgetPath } from '@/components/editor/widget-tree'
import { useDebounce } from '@/hooks/use-debounce'
import { ViewerConfig } from "@/components/Viewer"
import { HostedViewerCtx } from "@/hooks/use-app-ctx"
import { saveViewerSnapshot } from '@/components/editor/widget-editor/undo-redo'
import { useLocalSettings } from '@/components/editor/local-settings'

/** What a field reports about its own value, e.g. Table++ validating its column config */
export type FieldFeedback = { kind: 'warning' | 'error', message: React.ReactNode }
type FeedbackByField = Record<string, FieldFeedback>

/** A draft with errors, parked while another widget is selected */
type HeldDraft = {
  configOriginal: WidgetConfig | null
  configDraft: WidgetConfig
  feedback: FeedbackByField
}

type EditorStoreType = {
  path: string | null
  configOriginal: WidgetConfig | null
  configDraft: WidgetConfig | null
  touchedFields: Set<string>
  feedback: FeedbackByField
  held: Record<string, HeldDraft>

  reset: (config: WidgetConfig | null, path: string | null) => void
  updateField: (key: string, value: unknown) => void
  setFeedback: (field: string, kind: 'valid' | 'warning' | 'error', message?: React.ReactNode) => void
}

const NO_FEEDBACK: FeedbackByField = {}

const useWidgetEditorStore = create<EditorStoreType>()((set, get) => ({
  path: null,
  configOriginal: null,
  configDraft: null,
  touchedFields: new Set(),
  feedback: NO_FEEDBACK,
  held: {},
  reset: (config: WidgetConfig | null, path: string | null) => {
    const st = get()
    if (path === st.path && config === st.configOriginal) return // every controller instance resets on the same change

    const held = { ...st.held }
    // an invalid draft is never applied, so keep it for when this widget is selected again
    if (st.path && st.path !== path && st.configDraft && hasErrors(st.feedback)) {
      held[st.path] = { configOriginal: st.configOriginal, configDraft: st.configDraft, feedback: st.feedback }
    }
    const parked = path != null ? held[path] : undefined
    if (path != null) delete held[path]

    // restore only when the widget did not change meanwhile (an undo, another panel)
    const restore = parked && parked.configOriginal === config && path !== st.path
    set({
      path,
      configOriginal: config,
      configDraft: restore ? parked.configDraft : config,
      feedback: restore ? parked.feedback : NO_FEEDBACK,
      touchedFields: new Set(),
      held,
    })
  },
  setFeedback: (field, kind, message) => {
    const { feedback } = get()
    if (kind === 'valid') {
      if (!(field in feedback)) return
      const { [field]: _fixed, ...rest } = feedback
      set({ feedback: rest })
    } else {
      set({ feedback: { ...feedback, [field]: { kind, message } } })
    }
  },
  updateField: (field: string, value: unknown) => {
    set(produce(draft => {
      draft.touchedFields.add(field)
      if (field === 'widget') {
        mutateWidgetTypeWithDefaults(draft.configDraft, value)
      } else {
        draft.configDraft[field] = value
      }
    }))
  }
}))

export function useWistorEditorController(
  viewerConfig: ViewerConfig,
  viewerCtx: HostedViewerCtx
) {
  const selectedPath = useWidgetSelectorStore(s => s.selectedPath)
  const selectedInfo = useSelectedWidgetInfo(selectedPath, viewerConfig.mainArea)
  const selectedConfig = selectedInfo.current.config ?? null
  const configDraft = useWidgetEditorStore(st => st.configDraft)
  const debounceTime = useLocalSettings(st => (st.liveUpdateDelay * 1000) ?? 200)

  useEffect(() => {
    useWidgetEditorStore.getState().reset(selectedConfig, selectedPath)
  }, [ selectedConfig, selectedPath ])

  useAutoPublishLiveUpdates({
    selectedPath,
    selectedConfig,
    configDraft,
    mainArea: viewerConfig.mainArea,
    debounceTime,
    viewerCtx
  })

  const updateField = useWidgetEditorStore(st => st.updateField)
  const setFeedback = useWidgetEditorStore(st => st.setFeedback)
  const feedback = useWidgetEditorStore(st => st.feedback)

  return {
    selectedPath,
    selectedInfo: selectedInfo.current,
    configDraft,
    updateField,
    feedback,
    setFeedback,
  } as const
}

type SelectedWidgetInfo = {
  path: string,
  config: WidgetConfig
  isInitialized: true
} | {
  path: string,
  config: null,
  isInitialized: false
} | {
  path: null,
  config: null
}
const NO_SELECTION = { path: null, config: null } satisfies SelectedWidgetInfo

function useSelectedWidgetInfo(selectedPath: string | null, mainArea: WidgetConfig) {
  const selectedConfigRef = useRef<SelectedWidgetInfo>(NO_SELECTION)
  selectedConfigRef.current = (() => {
    if (!selectedPath) return NO_SELECTION

    const config = getAreaByWidgetPath(selectedPath, mainArea)
    return config == null
      ? {
        config: null,
        path: selectedPath,
        isInitialized: false
      } as const : {
        config,
        path: selectedPath,
        isInitialized: true
      } as const
  })()

  return selectedConfigRef
}

function useAutoPublishLiveUpdates(params: {
  selectedPath: string | null,
  selectedConfig: WidgetConfig | null,
  configDraft: WidgetConfig | null,
  mainArea: WidgetConfig,
  debounceTime: number,
  viewerCtx: HostedViewerCtx
}) {
  const { selectedPath, selectedConfig, configDraft } = params
  // a draft a field reports as invalid stays unapplied; fixing it applies the pending change
  const blocked = useWidgetEditorStore(st => hasErrors(st.feedback))
  useDebounce(() => {
    if (configDraft == null) return
    if (configDraft === selectedConfig) return
    if (!selectedPath) return
    if (hasErrors(useWidgetEditorStore.getState().feedback)) return

    const description = `${[ ...useWidgetEditorStore.getState().touchedFields].join(', ')} field change`

    const newConfig = selectedPath === "mainArea"
      ? configDraft
      : produce(params.mainArea, draft => {
        mutateAreaByWidgetPath(selectedPath, draft, configDraft)
      })
    const newViewer = params.viewerCtx.updateMainArea(newConfig)
    saveViewerSnapshot(newViewer, description)

  }, params.debounceTime, [ configDraft, blocked ])
}

function hasErrors(feedback: FeedbackByField) {
  return Object.values(feedback).some(f => f.kind === 'error')
}

/** Edits that are not applied because a field reports an error, for the selected widget or one parked */
export function hasUnappliedInvalidEdits() {
  const st = useWidgetEditorStore.getState()
  return hasErrors(st.feedback) || Object.keys(st.held).length > 0
}
